// @ts-strict-ignore
import * as React from 'react';
import { FeatureSection, Nullable, ReleaseComponentProps, ReleaseStepComponentProps } from '@/typescriptHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { displayStatusForPRStatus, isDoneTranslationStatus, tagColorForPullRequestStatus } from '@/helpers';
import { RootState } from '@/reducers/reducers';
import {
    selectAppSettings,
    selectAutomationById,
    selectTranslationsIntegration,
    selectVCSIntegration,
} from '@/selectors/selectors';
import {
    AutomationType,
    IntegrationType,
    PullRequest,
    ReleaseStepType,
    TranslationProviderState,
    TranslationString,
    TranslationStringStatus,
    TranslationValue,
} from '@/types';
import Link from 'antd/lib/typography/Link';
import List from 'antd/lib/list';
import Icon, {
    BranchesOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    CloseCircleFilled,
    CloudSyncOutlined,
    CloudUploadOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    PullRequestOutlined,
    StarFilled,
    ThunderboltFilled,
} from '@ant-design/icons';
import { antGrey, colorWithAlpha, runwayColors, rwColors, tagStyle } from '@/components/styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Tabs from 'antd/lib/tabs';
import Space from 'antd/lib/space';
import styles from '../styles/stepStyles';
import Tooltip from 'antd/lib/tooltip';
import Search from 'antd/lib/input/Search';
import CheckableTag from 'antd/lib/tag/CheckableTag';
import { ConfigProvider, Tag } from 'antd/lib';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import {
    selectedProjectNameForTranslationsIntegration,
    selectedProjectURLForTranslationsIntegration,
} from '@/helpers/displayHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { Link as RouterLink } from 'react-router-dom';
import { createAppSettingsAutomationsRouteWithAutomation } from '@/helpers/routeHelpers';
import Divider from 'antd/lib/divider';
import { CommitAuthorAvatarAndName } from '../buildDistribution/CommonComponents';
import SmallStatusNode from '../SmallStatusNode';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { tryUpdateReleaseStep } from '@/actions/releaseStep';
import { getRelevantTranslationStrings } from '@/components/StepContentSubmission/helpers';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface TranslationStringsProps extends ReleaseStepComponentProps {
    workingBranchEnabled: boolean;
    mergeRunwayPRsEnabled: boolean;
    releaseBranchName: string;
    stepRunId: string;
    sourceLocale: string;
    latestPullRequest: Nullable<PullRequest>;
    providerState: Nullable<TranslationProviderState>;
    keys: TranslationString[];
    workingBranchKeys: TranslationString[];
    autoSyncTranslationsAutomationId: Nullable<string>;
}

export const TranslationStrings: React.FC<TranslationStringsProps> = (props) => {
    const translationsIntegration = useSelector((state: RootState) =>
        selectTranslationsIntegration(state, props.appId),
    );

    const sourceLocale = props.sourceLocale;
    const latestPR = props.latestPullRequest;
    const providerState = props.providerState;
    const allStrings = getRelevantTranslationStrings(props.workingBranchEnabled, props.workingBranchKeys, props.keys);
    const pendingItems = allStrings.filter((string) => !isDoneTranslationStatus(string.status));

    const doneItems = allStrings.filter((string) => isDoneTranslationStatus(string.status));

    const autoSyncTranslations = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoSyncTranslationsAutomationId),
    );
    const workingBranch = useSelector((state: RootState) => selectAppSettings(state, props.appId).workingBranch);

    const [shouldShowNewAndUpdatedOnly, setShouldShowNewAndUpdatedOnly] = React.useState<boolean>(false);
    const [searchValue, setSearchValue] = React.useState<string>('');
    const filteredPendingItems = React.useMemo(() => {
        return pendingItems.filter((item) =>
            filterTranslationString(item, shouldShowNewAndUpdatedOnly, searchValue, sourceLocale),
        );
    }, [pendingItems, shouldShowNewAndUpdatedOnly, searchValue]);

    const filteredDoneItems = React.useMemo(() => {
        return doneItems.filter((item) =>
            filterTranslationString(item, shouldShowNewAndUpdatedOnly, searchValue, sourceLocale),
        );
    }, [doneItems, shouldShowNewAndUpdatedOnly, searchValue]);

    const stringHasStatus = (translationString: TranslationString, status: TranslationStringStatus) => {
        return translationString.status === status || translationString.values.some((value) => value.status === status);
    };

    const uploadNeededCount = React.useMemo(() => {
        return pendingItems.filter((item) => stringHasStatus(item, TranslationStringStatus.UploadNeeded)).length;
    }, [pendingItems]);

    const exportNeededCount = React.useMemo(() => {
        return pendingItems.filter((item) => stringHasStatus(item, TranslationStringStatus.ExportNeeded)).length;
    }, [pendingItems]);

    const defaultActiveKey = pendingItems.length > 0 ? 'pending' : 'done';

    return (
        <>
            <Space size={20} direction="vertical">
                {translationsIntegration && (
                    <TranslationsProviderStatus
                        releaseId={props.releaseId}
                        appId={props.appId}
                        orgId={props.orgId}
                        stepRunId={props.stepRunId}
                        branchName={props.releaseBranchName}
                        autoSyncTranslationsEnabled={autoSyncTranslations?.enabled ?? false}
                        autoSyncTranslationsName={autoSyncTranslations?.name ?? ''}
                        pendingItemsCount={pendingItems.length}
                        doneItemsCount={doneItems.length}
                        exportNeededCount={exportNeededCount}
                        uploadNeededCount={uploadNeededCount}
                        integrationId={translationsIntegration.id}
                        integrationName={translationsIntegration.name}
                        selectedProjectName={selectedProjectNameForTranslationsIntegration(translationsIntegration)}
                        selectedProjectURL={selectedProjectURLForTranslationsIntegration(translationsIntegration)}
                        providerState={providerState}
                    />
                )}
                {latestPR && <TranslationPRComponent pullRequest={latestPR} appId={props.appId} />}
                <Tabs
                    style={{ height: '100%', overflow: 'unset' }}
                    defaultActiveKey={defaultActiveKey}
                    tabBarStyle={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: runwayColors.white,
                        marginBottom: 0,
                    }}
                    tabBarExtraContent={{
                        right: (
                            <div
                                style={{
                                    padding: 12,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <Space>
                                    <CheckableTag
                                        checked={shouldShowNewAndUpdatedOnly}
                                        onChange={(checked) => setShouldShowNewAndUpdatedOnly(checked)}
                                        style={{
                                            borderColor:
                                                shouldShowNewAndUpdatedOnly ? runwayColors.primary : antGrey.grey5,

                                            backgroundColor:
                                                shouldShowNewAndUpdatedOnly ?
                                                    colorWithAlpha(rwColors.primary, 0.2)
                                                :   runwayColors.white,
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Space align="center" size={4}>
                                            <StarFilled
                                                style={{
                                                    color:
                                                        shouldShowNewAndUpdatedOnly ?
                                                            runwayColors.primary
                                                        :   antGrey.grey6,
                                                    fontSize: 12,
                                                }}
                                            />
                                            <Text
                                                style={{
                                                    fontSize: 12,
                                                    color:
                                                        shouldShowNewAndUpdatedOnly ?
                                                            runwayColors.primary
                                                        :   runwayColors.darkTextGrey,
                                                }}
                                            >
                                                Show new and updated only
                                            </Text>
                                        </Space>
                                    </CheckableTag>
                                    <Search
                                        placeholder="Search"
                                        allowClear
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        onSearch={(value) => {
                                            setSearchValue(value);
                                        }}
                                        style={{ width: 280 }}
                                    />
                                </Space>
                            </div>
                        ),
                    }}
                    className="fr-tabs"
                >
                    <Tabs.TabPane
                        tab={
                            <div style={styles.featureReadinessSectionHeader}>
                                <Text style={{ fontSize: 16 }}>Pending</Text>
                                <Text style={{ marginLeft: 8, fontSize: 16 }} type="secondary">
                                    ({pendingItems.length})
                                </Text>
                            </div>
                        }
                        key="pending"
                        style={{ overflow: 'unset' }}
                    >
                        <TranslationStringsList
                            orgId={props.orgId}
                            appId={props.appId}
                            releaseId={props.releaseId}
                            workingBranchName={workingBranch}
                            translationStrings={filteredPendingItems}
                            sourceLocale={sourceLocale}
                            integrationName={translationsIntegration?.name || ''}
                            releaseBranchName={props.releaseBranchName}
                            mergeRunwayPRsEnabled={props.mergeRunwayPRsEnabled}
                            releaseStepType={props.releaseStepType}
                            emptyText={'No pending translation strings.'}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={
                            <div style={styles.featureReadinessSectionHeader}>
                                <Text style={{ fontSize: 16 }}>Done</Text>
                                <Text style={{ marginLeft: 8, fontSize: 16 }} type="secondary">
                                    ({doneItems.length})
                                </Text>
                                <Tooltip
                                    title="Items are considered Done when all locales in the translations service have been translated, and the code on the branch has been updated to match."
                                    style={{ marginLeft: 8 }}
                                >
                                    <InfoCircleOutlined style={{ color: runwayColors.info, marginLeft: 8 }} />
                                </Tooltip>
                            </div>
                        }
                        style={{ overflow: 'unset' }}
                        key="done"
                    >
                        <TranslationStringsList
                            orgId={props.orgId}
                            appId={props.appId}
                            releaseId={props.releaseId}
                            translationStrings={filteredDoneItems}
                            integrationName={translationsIntegration?.name || ''}
                            sourceLocale={sourceLocale}
                            workingBranchName={workingBranch}
                            releaseBranchName={props.releaseBranchName}
                            mergeRunwayPRsEnabled={props.mergeRunwayPRsEnabled}
                            releaseStepType={props.releaseStepType}
                            emptyText={'No done translation strings.'}
                        />
                    </Tabs.TabPane>
                </Tabs>
            </Space>
        </>
    );
};

interface TranslationsProviderStatusProps extends ReleaseComponentProps {
    pendingItemsCount: number;
    doneItemsCount: number;
    uploadNeededCount: number;
    exportNeededCount: number;
    releaseId: string;
    branchName: string;
    autoSyncTranslationsEnabled: boolean;
    autoSyncTranslationsName: string;
    selectedProjectName?: string;
    selectedProjectURL?: string;
    integrationId?: IntegrationType;
    integrationName?: string;
    providerState?: TranslationProviderState;
    stepRunId: string;
}

export const TranslationsProviderStatus: React.FC<TranslationsProviderStatusProps> = (props) => {
    const syncTranslationsIsLoading = useIsLoading(ReleaseStepUpdatable.SyncTranslationStrings, props.stepRunId);
    const syncTranslationsError = useError(ReleaseStepUpdatable.SyncTranslationStrings, props.stepRunId);
    const syncTranslationsUpdated = useUpdated(ReleaseStepUpdatable.SyncTranslationStrings, props.stepRunId);
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (syncTranslationsError) {
            globalMessage.error(syncTranslationsError);
        }
    }, [syncTranslationsError]);

    React.useEffect(() => {
        if (syncTranslationsUpdated) {
            globalMessage.success('Translations synced successfully');
        }
    }, [syncTranslationsUpdated]);

    const onClickSyncTranslations = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.SyncTranslationStrings,
                {
                    appId: props.appId,
                    releaseId: props.releaseId,
                    orgId: props.orgId,
                    stepRunId: props.stepRunId,
                },
                {
                    branchName: props.branchName,
                },
                props.stepRunId,
            ),
        );
    };

    const exportNeededCount = props.exportNeededCount;
    const uploadNeededCount = props.uploadNeededCount;

    const renderSyncInfo = (exportNeededCount: number, uploadNeededCount: number) => {
        let tagText = '';
        let tagColor = '';

        let exportText = null;
        let uploadText = null;

        if (exportNeededCount > 0) {
            exportText = `${exportNeededCount} key${exportNeededCount > 1 ? 's' : ''} ${
                exportNeededCount > 1 ? 'are' : 'is'
            } ready to be exported from ${props.integrationName ?? 'the provider'}`;
        }

        if (uploadNeededCount > 0) {
            uploadText = `${uploadNeededCount} key${uploadNeededCount > 1 ? 's' : ''} need${
                uploadNeededCount > 1 ? '' : 's'
            } to be uploaded for translation`;
        }

        const isSynced = exportNeededCount === 0 && uploadNeededCount === 0;
        if (isSynced) {
            tagText = 'synced';
            tagColor = runwayColors.statusGreen;
        } else {
            tagText = 'not synced';
            tagColor = runwayColors.statusYellow;
        }

        return (
            <Tooltip
                title={
                    isSynced ? null : (
                        <>
                            {exportText && `- ${exportText}`}
                            {exportText && <br />}
                            {uploadText && `- ${uploadText}`}
                            <br />
                            Click Sync to perform the necessary actions. If an export is performed a pull request will
                            be opened against the release branch.
                        </>
                    )
                }
            >
                <Tag style={{ ...tagStyle, color: tagColor, borderColor: tagColor }} color={runwayColors.white}>
                    {tagText}
                </Tag>
            </Tooltip>
        );
    };

    const automationsRoute = createAppSettingsAutomationsRouteWithAutomation(
        props.orgId,
        props.appId,
        AutomationType.AutoSyncTranslations,
    );

    const hasProviderInfo = !!props.providerState;

    const keyCount = props.providerState?.keyCount ?? {};

    // Check if all locales are translated
    const allTranslated = React.useMemo(() => {
        return keyCount ?
                Object.keys(keyCount).reduce((acc, locale) => {
                    const totalKeyCount = keyCount[locale] ?? 0;
                    const translatedKeyCount = props.providerState?.translatedKeyCount[locale] ?? 0;
                    const isTranslated = totalKeyCount > 0 && totalKeyCount === translatedKeyCount;
                    return acc && isTranslated;
                }, true)
            :   false;
    }, [keyCount, props.providerState?.translatedKeyCount]);

    let providerTooltipTitle: string;
    if (allTranslated) {
        providerTooltipTitle = `All keys are translated and approved`;
        if (props.providerState?.branchName) {
            providerTooltipTitle += ` on the ${props.providerState.branchName} branch`;
        }
        if (props.integrationName) {
            providerTooltipTitle += ` in ${props.integrationName}.`;
        }
    } else {
        providerTooltipTitle = `Some keys are not translated or approved`;
        if (props.providerState?.branchName) {
            providerTooltipTitle += ` on the ${props.providerState.branchName} branch`;
        }
        if (props.integrationName) {
            providerTooltipTitle += ` in ${props.integrationName}.`;
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
            <Text style={{ ...tagStyle, color: antGrey.grey7, fontWeight: 400 }}>
                {props.integrationName ?? 'Translations provider'} status
            </Text>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: antGrey.grey2,
                    border: '1px solid',
                    borderRadius: '8px',
                    borderColor: antGrey.grey4,
                    padding: '16px 12px',
                }}
            >
                <Space>
                    {hasProviderInfo ?
                        <Tooltip
                            title={
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '2px',
                                    }}
                                >
                                    <div
                                        style={{
                                            marginBottom: '2px',
                                        }}
                                    >
                                        {providerTooltipTitle}
                                    </div>
                                    {Object.keys(keyCount).map((locale, index) => {
                                        const totalKeyCount = keyCount[locale] ?? 0;
                                        const translatedKeyCount = props.providerState?.translatedKeyCount[locale] ?? 0;
                                        const percentTranslated =
                                            totalKeyCount ? (translatedKeyCount / totalKeyCount) * 100 : 0;
                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {locale}: {translatedKeyCount} out of {totalKeyCount} (
                                                {percentTranslated.toFixed(0)}%)
                                            </div>
                                        );
                                    })}
                                </div>
                            }
                        >
                            <Space>
                                <SmallStatusNode
                                    size={6}
                                    color={allTranslated ? runwayColors.statusGreen : runwayColors.statusYellow}
                                />
                                {props.integrationId && (
                                    <IntegrationIcon integrationId={props.integrationId} size={24} />
                                )}
                            </Space>
                        </Tooltip>
                    :   props.integrationId && <IntegrationIcon integrationId={props.integrationId} size={24} />}
                    <Link style={{ fontSize: '16px', fontWeight: 600 }} href={props.selectedProjectURL} target="_blank">
                        {props.selectedProjectName ?? 'Translations project'}
                    </Link>
                </Space>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {(props.doneItemsCount > 0 || props.pendingItemsCount > 0) &&
                        renderSyncInfo(exportNeededCount, uploadNeededCount)}
                    <PermissionedActionButton
                        disabled={exportNeededCount == 0 && uploadNeededCount == 0}
                        actionType={RunwayUserAction.UAImportExportTranslationStrings}
                        loading={syncTranslationsIsLoading}
                        onClick={onClickSyncTranslations}
                        type="primary"
                        icon={<CloudSyncOutlined />}
                    >
                        Sync
                    </PermissionedActionButton>
                    <Tooltip
                        title={
                            <>
                                <p>
                                    With the Sync action, Runway will intelligently perform any necessary upload or
                                    export (or both) as needed, based on the current state of your localizable strings.
                                </p>
                                <p style={{ marginBottom: 0 }}>
                                    This may also result in a PR if Runway detects that there are new translations that
                                    need to be added to your repo.
                                </p>
                            </>
                        }
                    >
                        <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey, fontSize: 16 }} />
                    </Tooltip>
                    {props.autoSyncTranslationsEnabled && (
                        <Tooltip
                            title={
                                <>
                                    ‘{props.autoSyncTranslationsName}’ automation is enabled.{' '}
                                    <RouterLink
                                        style={{ color: runwayColors.white, textDecoration: 'underline' }}
                                        to={automationsRoute}
                                    >
                                        View settings
                                    </RouterLink>
                                </>
                            }
                        >
                            <ThunderboltFilled style={{ color: runwayColors.primary, fontSize: 20 }} />
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

export const TranslationStringsList: React.FC<{
    orgId: string;
    appId: string;
    releaseId: string;
    workingBranchName: string;
    translationStrings: TranslationString[];
    integrationName: string;
    mergeRunwayPRsEnabled: boolean;
    sourceLocale: string;
    emptyText: string;
    releaseBranchName?: string;
    releaseStepType?: ReleaseStepType;
}> = (props) => {
    const renderItem = (translationString: TranslationString) => {
        return (
            <List.Item
                style={{
                    padding: 0,
                    width: '100%',
                }}
            >
                <TranslationStringItem
                    orgId={props.orgId}
                    appId={props.appId}
                    releaseId={props.releaseId}
                    translationString={translationString}
                    integrationName={props.integrationName}
                    workingBranchName={props.workingBranchName}
                    releaseBranchName={props.releaseBranchName}
                    mergeRunwayPRsEnabled={props.mergeRunwayPRsEnabled}
                    sourceLocale={props.sourceLocale}
                    openPRNumber={translationString.openPRNumber}
                    exportPRNumber={translationString.exportPRNumber}
                />
            </List.Item>
        );
    };

    const { translationStrings } = props;
    return (
        <EmptyStateWrapper
            appId={props.appId}
            releaseId={props.releaseId}
            featureSections={[FeatureSection.TranslationStrings]}
            releaseStepType={props.releaseStepType}
            shouldForceDisable={translationStrings.length > 0}
        >
            {(isInEmptyState) => {
                return translationStrings.length > 0 ?
                        <List
                            size="small"
                            pagination={{
                                hideOnSinglePage: true,
                                defaultPageSize: 10,
                            }}
                            renderItem={renderItem}
                            itemLayout={'horizontal'}
                            dataSource={translationStrings}
                            style={{ width: '100%' }}
                        />
                    :   <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '350px',
                            }}
                        >
                            {!isInEmptyState && <Text>No pending translation strings.</Text>}
                        </div>;
            }}
        </EmptyStateWrapper>
    );
};

export const TranslationPRComponent: React.FC<{
    pullRequest: PullRequest;
    appId: string;
}> = ({ pullRequest, appId }) => {
    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, appId));
    const statusColor = tagColorForPullRequestStatus(pullRequest.status);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Text style={{ ...tagStyle, color: antGrey.grey7, fontWeight: 400 }}>Latest PR Status</Text>
            <div
                style={{
                    border: '1px solid',
                    borderColor: antGrey.grey5,
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '14px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingBottom: '10px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ gap: '10px', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {vcsIntegration?.id && <IntegrationIcon integrationId={vcsIntegration.id} size={20} />}
                            <Tag
                                style={{ ...tagStyle, borderColor: statusColor, fontSize: 10, color: statusColor }}
                                color={runwayColors.white}
                            >
                                {displayStatusForPRStatus(pullRequest.status)}
                            </Tag>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Link style={{ fontWeight: 600 }} href={pullRequest.url} target="_blank">
                                PR #{pullRequest.number}
                            </Link>
                            <Divider type="vertical" />
                            <BranchesOutlined style={{ color: antGrey.grey8, fontSize: 16, marginRight: '2px' }} />
                            <Text
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    color: antGrey.grey8,
                                }}
                                ellipsis
                            >
                                {pullRequest.baseBranch}
                            </Text>
                        </div>
                    </div>
                    <div style={{ marginLeft: 30, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Text>{pullRequest.title}</Text>
                        {pullRequest.filesChanged?.length > 0 && (
                            <Tooltip
                                title={
                                    <div
                                        style={{
                                            width: '300px',
                                            maxHeight: '500px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '2px',
                                        }}
                                    >
                                        {pullRequest.filesChanged.map((file, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        direction: 'rtl',
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    {file}
                                                </div>
                                            );
                                        })}
                                    </div>
                                }
                            >
                                <Space>
                                    <Text type="secondary">
                                        {pullRequest.filesChanged.length} file
                                        {pullRequest.filesChanged.length > 1 ? 's' : ''} changed
                                    </Text>
                                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                </Space>
                            </Tooltip>
                        )}
                    </div>
                </div>
                <CommitAuthorAvatarAndName commitAuthor={pullRequest.owner} />
            </div>
        </div>
    );
};

const TranslationStringItem: React.FC<{
    orgId: string;
    appId: string;
    releaseId: string;
    translationString: TranslationString;
    integrationName: string;
    workingBranchName: string;
    releaseBranchName: string;
    mergeRunwayPRsEnabled: boolean;
    sourceLocale: string;
    openPRNumber?: string;
    exportPRNumber?: string;
}> = (props) => {
    const { translationString } = props;
    const displayLocales = new Intl.DisplayNames('en-US', { type: 'language', style: 'narrow' });

    const firstLocale = translationString.values.length > 0 ? translationString.values[0].locale : 'en';
    const [activeLocale, setActiveLocale] = React.useState<string>(firstLocale);
    const baseTabStyle = {
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const activeTabStyle = {
        ...baseTabStyle,
        width: '114px',
    };

    const inactiveTabStyle = {
        ...baseTabStyle,
        width: '54px',
    };
    return (
        <div
            style={{
                display: 'flex',
                width: '100%',
            }}
        >
            <div
                style={{
                    width: '40%',
                    gap: 28,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    maxWidth: 380,
                    flexShrink: 0,
                    paddingLeft: 10,
                    paddingRight: 10,
                    borderRight: '1px solid',
                    borderRightColor: antGrey.grey4,
                }}
            >
                <Tooltip
                    title={tooltipForStatus(
                        translationString.status,
                        props.mergeRunwayPRsEnabled,
                        props.workingBranchName,
                        translationString.missingInProvider,
                        props.integrationName,
                        props.releaseBranchName,
                        props.openPRNumber,
                        props.exportPRNumber,
                    )}
                >
                    {iconForStatus(translationString.status, 16)}
                </Tooltip>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        minWidth: 0,
                    }}
                >
                    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                        {translationString.isNew && (
                            <Tooltip
                                title={
                                    'This key and its source value was either newly added or its value was updated in this release.'
                                }
                            >
                                <Icon component={StarFilled} style={{ fontSize: 16, color: antGrey.grey5 }} />
                            </Tooltip>
                        )}
                        <Text style={{ fontWeight: 600, color: runwayColors.primary }} ellipsis>
                            {translationString.key}
                        </Text>
                    </div>
                    {translationString.fileName && (
                        <Text type="secondary" ellipsis style={{ direction: 'rtl' }}>
                            {translationString.fileName}
                        </Text>
                    )}
                </div>
            </div>
            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            cardGutter: 0,
                            cardBg: antGrey.grey2,
                        },
                    },
                    token: {
                        borderRadiusLG: 0,
                        borderRadius: 0,
                    },
                }}
            >
                <Tabs
                    defaultActiveKey={firstLocale}
                    onChange={setActiveLocale}
                    type={'card'}
                    animated={false}
                    tabBarStyle={{
                        backgroundColor: antGrey.grey3,
                    }}
                    className="translation-locale-tab"
                >
                    {translationString.values
                        .reduce((acc, value) => {
                            if (acc.some((v) => v.locale === value.locale)) {
                                return acc;
                            }
                            return [...acc, value];
                        }, [])
                        .map((value) => {
                            const valueStr = value.value ? value.value : value.pluralValues?.one || '';
                            const isActive = activeLocale == value.locale;
                            return (
                                <Tabs.TabPane
                                    tab={
                                        <div style={isActive ? activeTabStyle : inactiveTabStyle}>
                                            <SmallStatusNode size={6} color={colorForStatus(value.status)} />
                                            {isActive && (
                                                <Tooltip
                                                    title={tooltipForValue(
                                                        value,
                                                        props.mergeRunwayPRsEnabled,
                                                        props.workingBranchName,
                                                        props.sourceLocale,
                                                        props.integrationName,
                                                        props.releaseBranchName,
                                                        props.openPRNumber,
                                                        props.exportPRNumber,
                                                    )}
                                                >
                                                    <Text
                                                        style={{
                                                            color: antGrey.grey8,
                                                            ...tagStyle,
                                                            fontWeight: 600,
                                                        }}
                                                        ellipsis
                                                    >
                                                        {displayLocales.of(new Intl.Locale(value.locale).language)}
                                                    </Text>
                                                </Tooltip>
                                            )}
                                            <Text style={{ ...tagStyle, color: antGrey.grey6, fontWeight: 400 }}>
                                                {value.locale}
                                            </Text>
                                        </div>
                                    }
                                    key={value.locale}
                                >
                                    <div style={{ padding: 14 }}>
                                        <Text>{valueStr}</Text>
                                    </div>
                                </Tabs.TabPane>
                            );
                        })}
                </Tabs>
            </ConfigProvider>
        </div>
    );
};

const colorForStatus: (status: TranslationStringStatus) => string = (status) => {
    switch (status) {
        case TranslationStringStatus.Synced:
            return runwayColors.statusGreen;
        case TranslationStringStatus.Inconclusive:
        case TranslationStringStatus.UnsupportedFileType:
            return antGrey.grey6;
        case TranslationStringStatus.TranslationsPending:
            return runwayColors.statusYellow;
        case TranslationStringStatus.OpenPR:
            return runwayColors.statusYellow;
        case TranslationStringStatus.UploadNeeded:
            return runwayColors.statusYellow;
        case TranslationStringStatus.ExportNeeded:
            return runwayColors.statusYellow;
        case TranslationStringStatus.BranchUpdateNeeded:
            return runwayColors.statusYellow;
        case TranslationStringStatus.BranchUpdatePending:
            return runwayColors.statusYellow;
        case TranslationStringStatus.BranchUpdatePROpen:
            return runwayColors.statusYellow;
        case TranslationStringStatus.BranchUpdateFailed:
            return runwayColors.statusRed;
    }
};

const tooltipForValue: (
    value: TranslationValue,
    mergeRunwayPRsEnabled: boolean,
    workingBranchName: string,
    sourceLocale: string,
    integrationName?: string,
    releaseBranchName?: string,
    openPRNumber?: string,
    exportPRNumber?: string,
) => React.ReactNode = (
    value,
    mergeRunwayPRsEnabled,
    workingBranchName,
    sourceLocale,
    integrationName,
    releaseBranchName,
    openPRNumber,
    exportPRNumber,
) => {
    if (!integrationName) {
        integrationName = 'the translations provider';
    }
    if (!releaseBranchName) {
        releaseBranchName = 'release';
    }
    switch (value.status) {
        case TranslationStringStatus.TranslationsPending:
            return `This string is still waiting for translation.`;
        case TranslationStringStatus.ExportNeeded:
            if (value.missingInVCS === true) {
                return `Export needed – This key exists in ${integrationName} but is not found in the codebase on the base branch. Use the 'Export strings' button to pull the latest keys and translations from ${integrationName}. A PR with the latest changes will be opened against the chosen branch.`;
            }
            return `This translation is ready to be exported.`;
        case TranslationStringStatus.Synced:
            return `This translation is synced with the ${releaseBranchName} branch.`;
        case TranslationStringStatus.OpenPR:
            return `Open PR — This translation is updated in an open pull request (PR #${openPRNumber}).`;
        case TranslationStringStatus.BranchUpdateNeeded:
            return exportPRNumber ?
                    `Branch update needed – This updated translation has been merged into the working branch (PR #${exportPRNumber}), but the change needs to be pulled over into the ${releaseBranchName} branch.`
                :   `This translation has been updated on the ${workingBranchName} branch, and needs to be pulled over into the ${releaseBranchName} branch.`;
        case TranslationStringStatus.BranchUpdatePending:
            return exportPRNumber ?
                    `Branch update pending – This updated translation has been merged into the working branch (PR #${exportPRNumber}). Runway will pull the change over to the ${releaseBranchName} branch.`
                :   `This translation has been updated on the ${workingBranchName} branch – Runway will pull the change over to the ${releaseBranchName} branch.`;
        case TranslationStringStatus.BranchUpdatePROpen:
            return `Branch update PR open – This updated translation has been merged into the working branch${
                exportPRNumber ? ` (PR ${exportPRNumber}` : ``
            }. Runway has opened a pull request (PR #${openPRNumber}) with the change against the ${releaseBranchName} branch. ${
                mergeRunwayPRsEnabled ?
                    `The PR will be automatically merged once all checks have passed.`
                :   `You must manually merge PR #${openPRNumber}.`
            }.`;
        case TranslationStringStatus.BranchUpdateFailed:
            return `The updated translation was merged into the working branch, but Runway failed to pull the change over from the ${workingBranchName} branch into the ${releaseBranchName} branch.`;
        default:
            return null;
    }
};

const tooltipForStatus: (
    status: TranslationStringStatus,
    mergeRunwayPRsEnabled: boolean,
    workingBranchName: string,
    missingInProvider: boolean,
    integrationName?: string,
    releaseBranchName?: string,
    openPRNumber?: string,
    exportPRNumber?: string,
) => React.ReactNode = (
    status,
    mergeRunwayPRsEnabled,
    workingBranchName,
    missingInProvider,
    integrationName,
    releaseBranchName,
    openPRNumber,
    exportPRNumber,
) => {
    if (!integrationName) {
        integrationName = 'the translations provider';
    }
    if (!releaseBranchName) {
        releaseBranchName = 'release';
    }
    switch (status) {
        case TranslationStringStatus.Synced:
            return `Synced – This key's source value matches ${integrationName}'s source value, and the translations in all languages match with what's found in the code.`;
        case TranslationStringStatus.Inconclusive:
            return `This key's source value matches ${integrationName}'s source value and the translations in all languages match what's found in the code on the base branch. No changes have been made to either the source value, nor the translations since the previous release, but the file this key belongs in is not yet fully translated according to ${integrationName}.`;
        case TranslationStringStatus.UnsupportedFileType:
            return `Unsupported file type – This locale's value exists in a file with an unsupported type. Runway is unable to parse localizable string values outside of the following supported file types: .strings, .stringsdict, .xcstrings, .xliff`;
        case TranslationStringStatus.TranslationsPending:
            return `Translations pending – Waiting for ${integrationName} to translate one or more languages for this key.`;
        case TranslationStringStatus.OpenPR:
            return `Open PR — This key and its updated translations are in an open pull request (PR #${openPRNumber}).`;
        case TranslationStringStatus.UploadNeeded:
            return `Upload needed – ${
                missingInProvider === true ?
                    `This is a new key that hasn't yet been uploaded to`
                :   `This key's source value in the code has diverged from the translation key's source value in`
            } ${integrationName}. Use the 'Upload strings' button to upload the latest localization files to ${integrationName} for translation.`;
        case TranslationStringStatus.ExportNeeded:
            return `Export needed – The key's source value matches ${integrationName}'s source value, and ${integrationName} is reporting that all languages have been translated, but one or more language values do not match with what's in the code. Use the 'Export translations' button to pull the latest translations from ${integrationName}. A PR with the latest changes will be opened against the chosen branch.`;
        case TranslationStringStatus.BranchUpdateNeeded:
            return exportPRNumber ?
                    `Branch update needed – This key and its updated translations have been merged into the working branch (PR #${exportPRNumber}), but the changes need to be pulled over into the ${releaseBranchName} branch.`
                :   `This key's translations have been updated on the ${workingBranchName} branch, and need to be pulled over into the ${releaseBranchName} branch.`;
        case TranslationStringStatus.BranchUpdatePending:
            return exportPRNumber ?
                    `Branch update pending – This key and its updated translations have been merged into the working branch (PR #${exportPRNumber}). Runway will pull the changes over to the ${releaseBranchName} branch.`
                :   `This key's translations have been updated on the ${workingBranchName} branch – Runway will pull the changes over to the ${releaseBranchName} branch.`;
        case TranslationStringStatus.BranchUpdatePROpen:
            return `Branch update PR open – This key and its updated translations have been merged into the working branch${
                exportPRNumber ? ` (PR ${exportPRNumber}` : ``
            }. Runway has opened a pull request (PR #${openPRNumber}) with the changes against the ${releaseBranchName} branch. ${
                mergeRunwayPRsEnabled ?
                    `The PR will be automatically merged once all checks have passed`
                :   `You must manually merge PR #${openPRNumber}`
            }.`;
        case TranslationStringStatus.BranchUpdateFailed:
            return `The key's updated translations were merged into the working branch, Runway failed to pull over localizable string changes from the working branch into the ${releaseBranchName} branch.`;
    }
};

const iconForStatus: (status: TranslationStringStatus, size: number) => React.ReactNode = (status, size) => {
    switch (status) {
        case TranslationStringStatus.Synced:
            return <CheckCircleFilled style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.Inconclusive:
        case TranslationStringStatus.UnsupportedFileType:
            return <CheckCircleFilled style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.TranslationsPending:
            return <ClockCircleFilled style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.OpenPR:
            return <PullRequestOutlined style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.UploadNeeded:
            return <CloudUploadOutlined style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.ExportNeeded:
            return <DownloadOutlined style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.BranchUpdateNeeded:
            return <PullRequestOutlined style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.BranchUpdatePending:
            return <PullRequestOutlined style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.BranchUpdatePROpen:
            return <PullRequestOutlined style={{ color: colorForStatus(status), fontSize: size }} />;
        case TranslationStringStatus.BranchUpdateFailed:
            return <CloseCircleFilled style={{ color: colorForStatus(status), fontSize: size }} />;
    }
};

const filterTranslationString = (
    translationString: TranslationString,
    showNewAndUpdatedOnly: boolean,
    searchValue: string,
    sourceLocale: string,
) => {
    const matchesNewAndUpdated = showNewAndUpdatedOnly ? translationString.isNew : true;
    const matchesKeySearch =
        searchValue === '' ? true : translationString.key.toLowerCase().includes(searchValue.toLowerCase());
    const matchesSourceLocaleSearch =
        searchValue === '' ? true : (
            translationString.values.some((v) => {
                if (v.locale !== sourceLocale) {
                    // looking for a source value match
                    return false;
                }
                if (v.value) {
                    return v.value.toLowerCase().includes(searchValue.toLowerCase());
                }
                if (v.pluralValues) {
                    return Object.values(v.pluralValues).some((pluralValue) =>
                        pluralValue.toLowerCase().includes(searchValue.toLowerCase()),
                    );
                }
                return false;
            })
        );

    const matchesSearch = matchesKeySearch || matchesSourceLocaleSearch;
    return matchesNewAndUpdated && matchesSearch;
};
